import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import BottomNav from '@components/BottomNav';
import TenantNav from '@components/TenantNav';
import { InquiryChatPanel } from '@components/InquiryChatPanel';
import { InquiryStatusBadge } from '@components/ui';
import { InquiryStatus } from '../../types/inquiry.types';

const InquiryChat: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const [propertyTitle, setPropertyTitle] = useState<string>('');
  const [status, setStatus] = useState<InquiryStatus | undefined>();

  useEffect(() => {
    if (!inquiryId) return;
    let cancelled = false;
    getDoc(doc(firestore, 'inquiries', inquiryId))
      .then((snapshot) => {
        if (!cancelled && snapshot.exists()) {
          const data = snapshot.data();
          setPropertyTitle((data.propertyTitle as string) || '');
          setStatus(data.status as InquiryStatus);
        }
      })
      .catch((err) => console.error('Error loading inquiry:', err));
    return () => {
      cancelled = true;
    };
  }, [inquiryId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex flex-col pb-16 md:pb-0">
      <div className="relative max-w-4xl mx-auto w-full px-3 md:px-4 pt-4 flex flex-col flex-1">
        <TenantNav />

        <div className="bg-white/90 backdrop-blur-sm border-2 border-gray-100 rounded-2xl mt-4 flex flex-col flex-1 overflow-hidden shadow-lg">
          {/* Header */}
          <div className="flex items-center gap-3 px-3 md:px-4 py-3 border-b-2 border-gray-100">
            <button
              onClick={() => navigate('/tenant/chat')}
              aria-label="Back to chats"
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-primary" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <Home className="w-4 h-4 text-primary-300 flex-shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-text-primary truncate">
                    {propertyTitle || 'Property inquiry'}
                  </p>
                  <InquiryStatusBadge status={status} />
                </div>
                <p className="text-xs text-text-secondary">Chat with the landlord</p>
              </div>
            </div>
          </div>

          {inquiryId && <InquiryChatPanel inquiryId={inquiryId} className="flex-1" />}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default InquiryChat;
